import { useEffect, useReducer } from 'react';
import apiClient from './api';

// Avatars rendered together ask once, in one batched GET agents/photos (ADR-0013); answers stay for the session and a
// missing photo or any failure caches null, so initials stay.
export const MAX_PHOTO_IDS = 100;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const defaultFetcher = (ids) => apiClient.get('/agents/photos', { params: { ids: ids.join(',') } }).then((response) => response.data || {});

let fetcher = defaultFetcher;
let cache = new Map();
let pending = new Set();
let inflight = new Set();
let timer = null;
const listeners = new Set();
const mounted = new Map();

const normalize = (id) => {
  const value = String(id || '').trim().toLowerCase();
  return GUID.test(value) ? value : null;
};

const notify = () => listeners.forEach((listener) => listener());

async function loadChunk(chunk) {
  chunk.forEach((id) => inflight.add(id));
  let photos = {};
  try {
    photos = (await fetcher(chunk)) || {};
  } catch (err) {
    photos = {};
  }
  const byId = new Map(Object.entries(photos).map(([key, value]) => [String(key).toLowerCase(), value]));
  chunk.forEach((id) => {
    const uri = byId.get(id);
    cache.set(id, typeof uri === 'string' && uri.startsWith('data:image/') ? uri : null);
    inflight.delete(id);
  });
  notify();
}

async function flush() {
  timer = null;
  const ids = [...pending];
  pending = new Set();
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_PHOTO_IDS) chunks.push(ids.slice(i, i + MAX_PHOTO_IDS));
  await chunks.reduce((previous, chunk) => previous.then(() => loadChunk(chunk)), Promise.resolve());
}

export function requestAgentPhotos(ids) {
  (ids || []).forEach((raw) => {
    const id = normalize(raw);
    if (id && !cache.has(id) && !inflight.has(id)) pending.add(id);
  });
  if (pending.size > 0 && timer === null) timer = setTimeout(flush, 0);
}

export function agentPhoto(id) {
  const key = normalize(id);
  return key ? cache.get(key) || null : null;
}

export function subscribeAgentPhotos(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Forget every answer, so the next avatars ask again (after the setting or a person's choice changes). */
export function clearAgentPhotos() {
  cache = new Map();
  requestAgentPhotos([...mounted.keys()]);
  notify();
}

/** Tests only: a fresh store with another fetcher. */
export function resetAgentPhotos(nextFetcher = defaultFetcher) {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  fetcher = nextFetcher;
  cache = new Map();
  pending = new Set();
  inflight = new Set();
  listeners.clear();
  mounted.clear();
}

export function useAgentPhoto(id) {
  const [, rerender] = useReducer((n) => n + 1, 0);
  const key = normalize(id);
  useEffect(() => {
    if (!key) return undefined;
    const unsubscribe = subscribeAgentPhotos(rerender);
    mounted.set(key, (mounted.get(key) || 0) + 1);
    requestAgentPhotos([key]);
    return () => {
      unsubscribe();
      const count = (mounted.get(key) || 1) - 1;
      if (count > 0) mounted.set(key, count);
      else mounted.delete(key);
    };
  }, [key]);
  return key ? agentPhoto(key) : null;
}
